import { Component, Inject, Injector, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import * as uuid from 'uuid';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';

import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { FilterService } from '../../../../Common/Filter/services/abstract-filter.service';
import { MentionSearchService } from '../../../Core/services/mention-search.service';
import { MentionsService } from '../../../Core/services/mentions.service';
import { SearchResultsService } from '../../../Core/services/search-results.service';
import { TargetingRhpContextsService } from '../../../../Common/TargetingShared/services/targeting-rhp-contexts.service';
import { TargetingRhpDialogService } from '../../../../Common/TargetingShared/services/targeting-rhp-dialog.service';
import { MentionTargetingRhpContextsService } from '../../../../Common/RhpContexts/services/mention-targeting-rhp-contexts.service';
import { ToolbarStateService } from '../../../../Common/TargetingShared/services/toolbar-state.service';
import { MentionsApi } from '../../../../api/mentions/mentions.api';
import { MentionsSearchResponseModel } from '../../../../api/mentions/model/mentions-search-response.model';
import { MentionSearchCriteriaModel } from '../../models/mention-search-criteria.model';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { SplitHeader } from '../../../../Common/TakeOver/components/TakeOverPanel/take-over-panel.component';
import { Mention } from '../../../../Common/Mentions/models/mention';
import { MonitoringViewModesEnum } from '../../enums/monitoring-view-modes.enum';
import { EntityType } from '../../../../api/targeting/enum/entity.type.enum';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { TargetingSearchService } from '../../../../Discover/Targeting/services/targeting-search.service';
import { TargetingSearch } from '../../../../Discover/Targeting/models/targeting-search.model';
import { SearchModeEnum } from '../../../../Common/TargetingShared/enum/search-mode.enum';
import { MentionSearchFacetTypesEnum as MentionSearchFacet } from '../../../../api/mentions/enum/mention-search-facet-types.enum';
import { EntityTakeoverModel } from '../../../../Common/RhpContexts/models/entity-takeover.model';
import { RightSidebarComponent } from '../../../../Common/Layout/components/RightSidebar/right-sidebar.component';
import { SearchMode } from '../../../Shared/enums/search-mode.enum';
import { MentionDetailsTakeOverPanelComponent } from
    '../../../../Common/Mentions/components/MentionDetailsTakeOverPanel/mention-details-take-over-panel.component';
import { DialogIoModel } from '../../../../Core/Dialogs/models/dialog-io.model';

@Component({
    selector: 'agpr-syndicated-mentions-take-over-panel',
    templateUrl: './syndicated-mentions-take-over-panel.component.html',
    styleUrls: ['./syndicated-mentions-take-over-panel.component.scss'],
    providers: [
        MentionSearchService,
        MentionsService,
        SearchResultsService,
        ToolbarStateService,
        TargetingRhpDialogService,
        TargetingSearchService,
        {
            provide: FilterService,
            useExisting: MentionSearchService
        },
        {
            provide: TargetingRhpContextsService,
            useClass: MentionTargetingRhpContextsService
        }
    ],
    standalone: false
})
export class SyndicatedMentionsTakeOverPanelComponent extends AbstractDialogComponent implements OnInit, OnDestroy {
    readonly buttonTypes = ButtonTypesEnum;
    readonly entityTypes = EntityType;
    readonly messageSeverityEnum = messageSeverityEnum;

    @Input() duplicateGroupId: string;
    @Input() searchCriteria: MentionSearchCriteriaModel;
    @Input() overlayContainer: Element;
    @Input() animationTrigger: BehaviorSubject<FullProfileAnimationModel>;

    @ViewChild('rhpTabs') rhpTabs: RightSidebarComponent;

    hideSlideOverlay: boolean = false;
    totalResults: number = 0;
    isLoading: boolean = false;
    selectedTabIndex: number = 0;
    focusedMention: Mention;
    mentions: Mention[] = [];
    originalMention: Mention | null = null;
    currentPage: number = 1;

    panelHeader: SplitHeader = {
        icon: '',
        initialText: 'Monitor.syndicates.panelTitle',
        value: '(0)',
        endText: '',
        loading: false
    };

    private currentSearchCriteria: MentionSearchCriteriaModel;
    private subscriptions: Subscription[] = [];

    constructor(
        public coreDialogService: CoreDialogService,
        public mentionSearchService: MentionSearchService,
        private mentionsService: MentionsService,
        private searchResultsService: SearchResultsService,
        private targetingRhpContextsService: TargetingRhpContextsService,
        private rhpDialogService: TargetingRhpDialogService,
        private targetingSearchService: TargetingSearchService,
        private mentionsApi: MentionsApi,
        private injector: Injector,
    ) {
        super(coreDialogService, uuid.v4());
    }

    ngOnInit(): void {
        if (!this.duplicateGroupId || !this.searchCriteria) {
            return;
        }

        this.initializeSyndicatedSearch();

        this.subscriptions.push(
            this.mentionSearchService.getSearchCriteria().subscribe(criteria => {
                this.currentSearchCriteria = criteria;
                this.currentPage = criteria?.page ?? 1;
            }),
            this.mentionSearchService.getMentionCount().subscribe(count => {
                this.totalResults = count;
                this.panelHeader.value = `(${count})`;
            }),

            combineLatest([
                this.mentionSearchService.getMentions(),
                this.fetchOriginalMention$(),
            ]).subscribe(([mentions, original]) => {
                this.originalMention = original;
                const all = mentions || [];
                const originalInResults = !!original && all.some(m =>
                    (!!original.id && m.id === original.id)
                    || (!!original.mentionId && m.mentionId === original.mentionId)
                );
                const deduped = originalInResults
                    ? all.filter(m =>
                        !((!!original.id && m.id === original.id)
                            || (!!original.mentionId && m.mentionId === original.mentionId))
                    )
                    : all;
                const newMentions = originalInResults && this.currentPage === 1
                    ? [original, ...deduped]
                    : deduped;
                this.mentionsService.setMentions(newMentions);
            }),
            this.mentionsService.getMentions().subscribe(mentions => {
                this.mentions = mentions;
            }),
            this.mentionsService.getFocusedMention().subscribe(mention => {
                this.focusedMention = mention;
                if (mention?.author?.uuid) {
                    this.targetingRhpContextsService.setFullProfileContactById(mention.author.uuid);
                } else {
                    this.targetingRhpContextsService.clearContact();
                }
                if (mention?.publication?.uuid) {
                    this.targetingRhpContextsService.setFullProfileOutletById(mention.publication.uuid);
                } else {
                    this.targetingRhpContextsService.clearOutlet();
                }
            })
        );
    }

    tabChange(tab: number): void {
        this.selectedTabIndex = tab;
        this.entitySearch();
    }

    openEntityTakeover(event: EntityTakeoverModel): void {
        this.rhpDialogService.openTakeOver({
            inputs: {
                contactId: event.contactId,
                outletId: event.outletId,
                entityType: event.entityType,
                isPrivate: event.isPrivate
            }
        });
    }

    private entitySearch(): void {
        if (!this.currentSearchCriteria?.query?.dateQuery) {
            return;
        }

        const tabs = this.rhpTabs?.tabs?.toArray();
        const contactTabIndex = tabs?.findIndex(t => t.id === 'syndicatedPanelRhpTab.contactList');
        const outletTabIndex = tabs?.findIndex(t => t.id === 'syndicatedPanelRhpTab.outletList');

        if (this.selectedTabIndex !== contactTabIndex && this.selectedTabIndex !== outletTabIndex) {
            return;
        }

        const search = new TargetingSearch();

        if (this.selectedTabIndex === contactTabIndex) {
            search.searchMode = SearchModeEnum.contact;
            if (!this.currentSearchCriteria.facets.includes(MentionSearchFacet.contactUuid)) {
                this.currentSearchCriteria.facets.push(MentionSearchFacet.contactUuid);
            }
            this.currentSearchCriteria.facets = this.currentSearchCriteria.facets.filter(
                f => f !== MentionSearchFacet.outletUuid
            );
        } else {
            search.searchMode = SearchModeEnum.outlet;
            if (!this.currentSearchCriteria.facets.includes(MentionSearchFacet.outletUuid)) {
                this.currentSearchCriteria.facets.push(MentionSearchFacet.outletUuid);
            }
            this.currentSearchCriteria.facets = this.currentSearchCriteria.facets.filter(
                f => f !== MentionSearchFacet.contactUuid
            );
        }

        this.currentSearchCriteria.dateFacets = [];

        search.advMonQuery = {
            searchRequestJSON: JSON.stringify(this.currentSearchCriteria.getApiEntity()),
            isSavedSearch: this.currentSearchCriteria.isAccountSearch
        };

        this.targetingSearchService.clearSearch();
        this.targetingSearchService.performSearch(search);
    }

    handleVisibleChange(isVisible: boolean): void {
        if (!isVisible) {
            this.closeDialog();
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(s => s.unsubscribe());
    }

    private initializeSyndicatedSearch(): void {
        const criteria = this.searchCriteria.clone();
        criteria.isAccountSearch = false;
        criteria.showSyndicates = true;
        criteria.query.syndicateIds = { include: [this.duplicateGroupId], exclude: [] };
        criteria.query.keywordGroups = [];
        // we never want to restrict it to a subscription
        criteria.query.subscription = {exclude:[], include:[]};
        criteria.query.searchMode = SearchMode.standard;
        criteria.query.dateQuery = this.mentionSearchService.getSyndicateMentionDateQuery(criteria.query.dateQuery);

        this.searchResultsService.setCurrentViewMode(MonitoringViewModesEnum.SYNDICATE);
        this.mentionSearchService.setShowSyndicates(true);
        this.mentionSearchService.setDateQuery(criteria.query.dateQuery);
        this.mentionSearchService.setSearchCriteria(criteria);
        this.mentionSearchService.search(true, true);
    }

    private fetchOriginalMention$(): Observable<Mention | null> {
        const search = this.searchCriteria.clone();
        search.showSyndicates = false;
        search.query.syndicateIds = { include: [this.duplicateGroupId], exclude: [] };
        search.query.keywordGroups = [];
        search.query.searchMode = SearchMode.standard;
        search.query.dateQuery = this.mentionSearchService.getSyndicateMentionDateQuery(search.query.dateQuery);
        search.neoFacets = [];
        search.facets = [];
        search.dateFacets = [];

        const apiEntity = search.getApiEntity();
        return this.mentionsApi.searchMentions(apiEntity).pipe(map((response: MentionsSearchResponseModel) => {
            const apiMention = response.mentions?.[0];
            return apiMention ? new Mention(apiMention) : null;
        }));
    }

    openMentionTakeOver(): void {
        const io: DialogIoModel = {
            inputs: {
                focusTab: 'mention',
                fromReports: true
            },
            outputs: {}
        };

        this.coreDialogService.open(MentionDetailsTakeOverPanelComponent, io, this.injector);
    }
}
